import React, { useMemo, useRef } from 'react';
import { formatLapTime, formatDelta, formatDate } from '../utils/format';

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 180,
    maxWidth: 220,
  },
  container: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    padding: '12px 14px',
  },
  lapScroll: {
    maxHeight: 400,
    overflowY: 'auto',
  },
  divider: {
    borderTop: '1px solid var(--color-border)',
    margin: '8px 0',
  },
  driverName: {
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 10,
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  actionRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  actionButton: (active) => ({
    padding: '4px 8px',
    background: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 5,
    color: active ? 'var(--color-text)' : 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    cursor: 'pointer',
  }),
  lapRow: (selected, isBest) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 6px',
    borderRadius: 4,
    cursor: 'pointer',
    background: selected ? 'rgba(46, 134, 193, 0.12)' : 'transparent',
    borderLeft: isBest ? '3px solid var(--color-accent)' : '3px solid transparent',
  }),
  checkbox: {
    width: 14,
    height: 14,
    accentColor: 'var(--color-accent)',
    cursor: 'pointer',
  },
  lapNum: {
    fontFamily: 'var(--font-data)',
    fontSize: 12,
    color: 'var(--color-text)',
    fontWeight: 600,
    minWidth: 28,
  },
  lapTime: {
    fontFamily: 'var(--font-data)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
  },
  delta: (val) => ({
    fontFamily: 'var(--font-data)',
    fontSize: 10,
    color: val <= 0 ? '#27ae60' : '#e74c3c',
    marginLeft: 'auto',
  }),
  bestBadge: {
    fontFamily: 'var(--font-heading)',
    fontSize: 8,
    color: 'var(--color-accent)',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginLeft: 4,
  },
  sortSelect: {
    padding: '4px 8px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 5,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    cursor: 'pointer',
    outline: 'none',
    marginBottom: 8,
  },
  compareSelect: {
    width: '100%',
    padding: '6px 8px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 5,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 11,
    cursor: 'pointer',
    outline: 'none',
    marginBottom: 8,
  },
};

// Theme presets for primary (blue) vs overlay (orange)
const THEMES = {
  primary: {
    accent: '#2e86c1',
    selectedBg: 'rgba(46, 134, 193, 0.12)',
  },
  overlay: {
    accent: '#e67e22',
    selectedBg: 'rgba(230, 126, 34, 0.12)',
  },
};

function titleCase(str) {
  if (!str) return str;
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

function sortLaps(laps, sortOrder) {
  const indexed = laps.map((lap, idx) => ({ lap, originalIdx: idx }));
  if (sortOrder === 'fastest') indexed.sort((a, b) => a.lap.lapTime - b.lap.lapTime);
  else if (sortOrder === 'slowest') indexed.sort((a, b) => b.lap.lapTime - a.lap.lapTime);
  return indexed;
}

const TRACE_COLORS = [
  '#2e86c1', '#e74c3c', '#27ae60', '#f39c12', '#9b59b6',
  '#1abc9c', '#e67e22', '#3498db', '#e91e63', '#00bcd4',
];

/** Shared lap grid used by both primary and comparison panels */
function LapGrid({ laps, bestLapIdx, selectedLaps, onToggleLap, sortOrder, theme, keyPrefix = '', colorOffset = 0 }) {
  const sorted = useMemo(() => sortLaps(laps, sortOrder), [laps, sortOrder]);
  const bestTime = laps[bestLapIdx]?.lapTime;
  const t = THEMES[theme] || THEMES.primary;

  return sorted.map(({ lap, originalIdx }) => {
    const isBest = originalIdx === bestLapIdx;
    const deltaFromBest = lap.lapTime - bestTime;
    const isSelected = selectedLaps.includes(originalIdx);

    return (
      <div
        key={`${keyPrefix}${originalIdx}`}
        style={{
          ...styles.lapRow(isSelected, isBest),
          background: isSelected ? t.selectedBg : 'transparent',
          borderLeftColor: isBest ? t.accent : 'transparent',
        }}
        onClick={() => onToggleLap(originalIdx)}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleLap(originalIdx)}
          style={{ ...styles.checkbox, accentColor: t.accent }}
          onClick={(e) => e.stopPropagation()}
        />
        <span style={{
          ...styles.lapNum,
          color: isSelected
            ? TRACE_COLORS[(colorOffset + selectedLaps.indexOf(originalIdx)) % TRACE_COLORS.length]
            : styles.lapNum.color,
        }}>L{lap.lapNum}</span>
        <span style={styles.lapTime}>{formatLapTime(lap.lapTime)}</span>
        {isBest && <span style={{ ...styles.bestBadge, color: t.accent }}>BEST</span>}
        {!isBest && (
          <span style={styles.delta(deltaFromBest)}>
            {formatDelta(deltaFromBest)}
          </span>
        )}
      </div>
    );
  });
}

export default function LapSelector({
  laps,
  bestLapIdx,
  selectedLaps,
  onToggleLap,
  onSelectBest,
  onSelectTopThree,
  onSelectAll,
  primaryLabel,
  overlayLaps,
  overlayBestLapIdx,
  selectedOverlayLaps,
  onToggleOverlayLap,
  overlayLabel,
  onRemoveOverlay,
  sortOrder = 'default',
  onSortChange,
  // Comparison session picker props
  compareSessions,
  compareLoading,
  onSelectCompareSession,
  onLoadCompareSessions,
  onOverlaySelectBest,
  onOverlaySelectTopThree,
  onOverlaySelectAll,
  overlayIsSameUser,
}) {
  const selectedCompareIdxRef = useRef('__none__');

  const handleCompareChange = (e) => {
    const val = e.target.value;
    selectedCompareIdxRef.current = val;
    if (val === '__none__') {
      onRemoveOverlay();
    } else {
      const idx = parseInt(val, 10);
      const cs = compareSessions[idx];
      if (cs) onSelectCompareSession(cs);
    }
  };

  const hasOverlay = overlayLaps?.length > 0;
  const dropdownValue = hasOverlay ? selectedCompareIdxRef.current : '__none__';

  // Determine which quick-select mode is active for primary panel
  const topThreeIdxs = useMemo(() => {
    if (!laps.length) return [];
    return laps
      .map((lap, idx) => ({ idx, lapTime: lap?.lapTime ?? Infinity }))
      .sort((a, b) => a.lapTime - b.lapTime)
      .slice(0, 3)
      .map((item) => item.idx);
  }, [laps]);

  const primaryMode = useMemo(() => {
    if (selectedLaps.length === 1 && selectedLaps[0] === bestLapIdx) return 'best';
    if (selectedLaps.length === laps.length && laps.length > 0) return 'all';
    if (selectedLaps.length === topThreeIdxs.length &&
        topThreeIdxs.every(i => selectedLaps.includes(i))) return 'top3';
    return null;
  }, [selectedLaps, bestLapIdx, laps, topThreeIdxs]);

  // Determine which quick-select mode is active for overlay panel
  const overlayTopThreeIdxs = useMemo(() => {
    if (!overlayLaps?.length) return [];
    return overlayLaps
      .map((lap, idx) => ({ idx, lapTime: lap?.lapTime ?? Infinity }))
      .sort((a, b) => a.lapTime - b.lapTime)
      .slice(0, 3)
      .map((item) => item.idx);
  }, [overlayLaps]);

  const overlayMode = useMemo(() => {
    if (!selectedOverlayLaps?.length || !overlayLaps?.length) return null;
    if (selectedOverlayLaps.length === 1 && selectedOverlayLaps[0] === overlayBestLapIdx) return 'best';
    if (selectedOverlayLaps.length === overlayLaps.length) return 'all';
    if (selectedOverlayLaps.length === overlayTopThreeIdxs.length &&
        overlayTopThreeIdxs.every(i => selectedOverlayLaps.includes(i))) return 'top3';
    return null;
  }, [selectedOverlayLaps, overlayBestLapIdx, overlayLaps, overlayTopThreeIdxs]);

  return (
    <div style={styles.wrapper}>
      {/* ── Primary Session Laps ── */}
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.title}>Laps</div>
          {onSortChange && (
            <select
              value={sortOrder}
              onChange={(e) => onSortChange(e.target.value)}
              style={{ ...styles.sortSelect, marginBottom: 0 }}
            >
              <option value="default">Default</option>
              <option value="fastest">Fastest</option>
              <option value="slowest">Slowest</option>
            </select>
          )}
        </div>
        <div style={styles.actionRow}>
          <button type="button" onClick={onSelectBest} style={styles.actionButton(primaryMode === 'best')}>Best</button>
          <button type="button" onClick={onSelectTopThree} style={styles.actionButton(primaryMode === 'top3')}>Top 3</button>
          <button type="button" onClick={onSelectAll} style={styles.actionButton(primaryMode === 'all')}>All</button>
        </div>
        <div style={styles.divider} />
        <div style={styles.lapScroll}>
          <LapGrid
            laps={laps}
            bestLapIdx={bestLapIdx}
            selectedLaps={selectedLaps}
            onToggleLap={onToggleLap}
            sortOrder={sortOrder}
            theme="primary"
          />
        </div>
      </div>

      {/* ── Comparison Laps (with session dropdown) ── */}
      <div data-doc-id="analysis-compare-open" style={{
        ...styles.container,
        borderColor: hasOverlay ? 'rgba(230, 126, 34, 0.3)' : 'var(--color-border)',
      }}>
        <div style={styles.headerRow}>
          <div style={{ ...styles.title, color: THEMES.overlay.accent }}>Comparison Laps</div>
        </div>

        {/* Session dropdown */}
        <select
          data-doc-id="analysis-compare-picker"
          value={dropdownValue}
          onChange={handleCompareChange}
          onFocus={() => {
            if (!compareSessions?.length && !compareLoading) {
              onLoadCompareSessions();
            }
          }}
          style={styles.compareSelect}
        >
          <option value="__none__">None</option>
          {compareLoading && <option disabled>Loading sessions...</option>}
          {compareSessions?.map((cs, idx) => (
            <option data-doc-id="analysis-compare-option" key={`${cs.ownerUid}-${cs.sessionId}`} value={idx}>
              {titleCase(cs.ownerName)} — {titleCase(cs.car)} — {formatDate(cs.date)} — {cs.lapCount} Laps — Best: {formatLapTime(cs.bestLap)}
            </option>
          ))}
        </select>

        {/* Overlay laps grid (shown when a comparison session is loaded) */}
        {hasOverlay ? (
          <>
            {!overlayIsSameUser && overlayLabel && (
              <div style={{ ...styles.driverName, color: THEMES.overlay.accent, marginBottom: 4, marginTop: 4 }}>
                {overlayLabel?.split(' - ')[0]}
              </div>
            )}
            <div style={styles.headerRow}>
              <div style={{ ...styles.title, color: THEMES.overlay.accent }}>Laps</div>
              {onSortChange && (
                <select
                  value={sortOrder}
                  onChange={(e) => onSortChange(e.target.value)}
                  style={{ ...styles.sortSelect, marginBottom: 0 }}
                >
                  <option value="default">Default</option>
                  <option value="fastest">Fastest</option>
                  <option value="slowest">Slowest</option>
                </select>
              )}
            </div>
            <div style={styles.actionRow}>
              <button type="button" onClick={onOverlaySelectBest} style={styles.actionButton(overlayMode === 'best')}>Best</button>
              <button type="button" onClick={onOverlaySelectTopThree} style={styles.actionButton(overlayMode === 'top3')}>Top 3</button>
              <button type="button" onClick={onOverlaySelectAll} style={styles.actionButton(overlayMode === 'all')}>All</button>
            </div>
            <div style={styles.divider} />
            <div style={styles.lapScroll}>
              <LapGrid
                laps={overlayLaps}
                bestLapIdx={overlayBestLapIdx}
                selectedLaps={selectedOverlayLaps || []}
                onToggleLap={onToggleOverlayLap}
                sortOrder={sortOrder}
                theme="overlay"
                keyPrefix="overlay-"
                colorOffset={selectedLaps.length}
              />
            </div>
          </>
        ) : (
          <div style={{
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'var(--color-text-dim)',
            fontStyle: 'italic',
            padding: '4px 0',
          }}>
            {!compareSessions?.length && !compareLoading
              ? 'No other sessions at this track'
              : 'Select a session to compare laps'}
          </div>
        )}
      </div>
    </div>
  );
}
